import React from "react";
import Link from "next/link";
import { Badge } from "@/v2-components/ui/badge";
import type { SupportCategory } from "@/v2-components/pages/support/types/support-data";
import ChevronUpIcon from "@/v2-components/core/icon/chevron-up-icon";
import { cn } from "@/libs/utils";
import {
  CategoryChildNode,
  useHelpCenterContext,
} from "@/v2-components/pages/support/context/help-center.context";
import ArticleIcon from "@/v2-components/core/icon/article-icon";
import { useRouter } from "next/router";

type SupportCategoryWithIsNew = SupportCategory & {
  isNew?: boolean;
};
interface HelpCenterSlideNodeProps {
  node: SupportCategoryWithIsNew;
  deps?: number;
  onCloseSlide?: () => void;
  parentIds?: string[]; // 부모 노드의 ID 목록을 전달받아 트리 구조를 추적
  query?: string;
}

/*
 help, faq header  공용 사용 slide node
 */
const HelpCenterSlideNode = ({
  node,
  deps = 0, //현재 deps
  onCloseSlide,
  parentIds = [],
  query = "categorySlug",
}: HelpCenterSlideNodeProps) => {
  const { openNodes, toggleNode } = useHelpCenterContext(); //openNodes : 열려 있는 노드 상태
  const currentNode = findNode(openNodes, node.id, parentIds); //하위노드시 재귀적으로 열린 현재 노드 추적
  const isOpen = currentNode?.isOpen || false;
  const router = useRouter();

  /* help, faq - page 사용 */
  const getDynamicPathname = (path: string): string => {
    if (path.includes("help")) {
      return "/support/help/";
    }
    if (path.includes("faq")) {
      return "/support/faq/";
    }
    return "";
  };

  const handleOnClose = () => {
    if (onCloseSlide) {
      onCloseSlide();
    }
  };

  const handleToggle = () => {
    toggleNode(node.id, parentIds); // 부모 ID 목록을 전달하여 열림 상태를 관리
  };
  const renderNodeContent = () => {
    const NormalNode = (
      <span
        className={cn("flex gap-2", {
          "font-600": isOpen,
        })}
        onClick={handleToggle}
      >
        <div className="max-w-xs truncate">{node.label}</div>
        {node.isNew && (
          <Badge variant="new" size="sm">
            New
          </Badge>
        )}
      </span>
    );

    if (node.id && !node.children?.length) {
      return (
        <div
          className={cn("flex cursor-pointer items-center gap-2", {
            "font-600": isOpen,
          })}
        >
          <div>
            <ArticleIcon className="size-3.5" />
          </div>

          <Link
            onClick={handleOnClose}
            href={{
              pathname: getDynamicPathname(router.pathname),
              query: { [query]: node.id },
            }}
            className="w-fit"
            shallow={true}
          >
            <span className="w-fit max-w-xs whitespace-normal break-words font-400">
              {node.label}
            </span>
          </Link>
          {node.isNew && (
            <div className="w-10">
              <Badge variant="new" size="sm">
                New
              </Badge>
            </div>
          )}
        </div>
      );
    }
    return NormalNode;
  };

  return (
    <div className={cn(getPaddingLeftClass(deps), "flex flex-col")}>
      <div className="flex items-center justify-between py-4 font-600 text-coolgray-100">
        {/*node contents*/}
        <div className="min-w-0 flex-grow">{renderNodeContent()}</div>

        {/*node arrow*/}
        {!!node.children?.length && (
          <span
            onClick={handleToggle}
            className={`ml-2 flex-shrink-0 transition-transform ${isOpen ? "rotate-0" : "rotate-180"}`}
          >
            <ChevronUpIcon />
          </span>
        )}
      </div>

      {/*node recursion */}
      {isOpen && node.children && (
        <div className="ml-4">
          {node.children.map((child) => (
            <HelpCenterSlideNode
              key={child.id}
              node={child}
              deps={deps + 1}
              onCloseSlide={handleOnClose}
              parentIds={[...parentIds, node.id]} // 부모 경로
              query={query}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HelpCenterSlideNode;

export function getPaddingLeftClass(deps: number | undefined): string {
  const paddingLevel = Math.min((deps ?? 0) + 16);
  return `pl-[${paddingLevel}]px`;
}

// 재귀적으로 현재  열린 노드 추적 함수
const findNode = (
  nodes: Record<string, CategoryChildNode>,
  id: string,
  parentIds: string[] = [],
): CategoryChildNode | undefined => {
  let currentNode = nodes;

  // parentIds가 없을 경우, 최상위 노드 id 할당
  for (const parentId of parentIds) {
    if (!currentNode[parentId]) return undefined;
    currentNode = currentNode[parentId].children;
  }

  return currentNode[id];
};
